import React, { ReactNode, useContext } from 'react';

import { FormControl, InputLabel, MenuItem, Select, Tooltip } from '@material-ui/core';
import tw, { styled } from 'twin.macro';

import { FadeInComponent } from '@/components/FadeInComponent';
import { LicenseType } from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import { LicenseTypeLabel } from '@/core/users/UserManagement/LicenseTypeLabel';
import { UserEditError } from '@/core/users/UserPermissions/userEditValidators';
import { ReactComponent as WarningIcon } from '@/icons/warning.svg';

type Props = {
  disabledMessage?: string;
  licenseType?: LicenseType;
  uvkAdmin?: boolean;
  onChange: (licenseType: LicenseType) => void;
  errorMessage?: string;
}
export function LicenseTypeSelect ({ disabledMessage, licenseType, uvkAdmin, onChange, errorMessage }: Props) {
  const { licensesRemaining, hasAccessTo } = useContext(AuthContext);
  const isUvkLicenseSelect = !licenseType && uvkAdmin;

  return (
    <Tooltip title={disabledMessage ? disabledMessage : ''}>
      <div>
        <FormControl variant="outlined" error={!!errorMessage} disabled={!!disabledMessage}>
          <InputLabel htmlFor="license-type-select">License</InputLabel>
          <Select
            autoWidth
            className="w-72 h-14"
            value={licenseType || uvkAdmin ? 'uvkAdmin' : ''}
            onChange={(e) => onChange(e.target.value as LicenseType)}
            label="License"
            inputProps={{
              id: 'license-type-select',
            }}
            renderValue={() => (
              <div className={`px-3 ${errorMessage ? 'text-error' : ''}`}>
                <LicenseTypeLabel
                  licenseType={licenseType}
                  uvkAdmin={uvkAdmin}
                />
              </div>
            )}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
            }}
          >
            <StyledMenuItem className={!isUvkLicenseSelect ? 'hidden' : ''} value="uvkAdmin" >
              <LicenseTypeListItem description="Can view and manage all roadmaps and features">
                <LicenseTypeLabel uvkAdmin={true} />
              </LicenseTypeListItem>
            </StyledMenuItem>
            <StyledMenuItem className={isUvkLicenseSelect ? 'hidden' : ''} value="fullaccess_admin">
              <LicenseTypeListItem
                description="Can use all UserVoice features"
                licensesRemaining={licensesRemaining?.admin}
              >
                <LicenseTypeLabel licenseType="fullaccess_admin" />
              </LicenseTypeListItem>
            </StyledMenuItem>
            <StyledMenuItem
              className={isUvkLicenseSelect || !hasAccessTo('license_types') ? 'hidden' : ''}
              value="contributor"
            >
              <LicenseTypeListItem
                description="Can capture and view customer feedback through the contributor sidebar and integrations."
                licensesRemaining={licensesRemaining?.contributor}
              >
                <LicenseTypeLabel licenseType="contributor" />
              </LicenseTypeListItem>
            </StyledMenuItem>c
            <StyledMenuItem
              className={isUvkLicenseSelect || !hasAccessTo('license_types') || !hasAccessTo('ticketing_system') ? 'hidden' : ''}
              value="helpdesk_admin"
            >
              <LicenseTypeListItem
                description="Can use only the ticketing and knowledge base."
                licensesRemaining={licensesRemaining?.support}
              >
                <LicenseTypeLabel licenseType="helpdesk_admin" />
              </LicenseTypeListItem>
            </StyledMenuItem>
            <MenuItem className={`border-0 bg-salmon-100 text-error ${errorMessage ? '' : 'hidden'}`}>
              <div className="break-words whitespace-normal grid gap-2.5 w-60">
                <div className="flex items-center font-medium">
                  <WarningIcon className="w-4 h-4 mr-4" />
                    Oops!
                </div>
                <LicenseTypeError errorMessage={errorMessage} />
              </div>
            </MenuItem>
          </Select>
          {errorMessage && (
            <FadeInComponent
              className="relative z-10 flex items-center p-3 my-2 text-sm font-light rounded-md bg-salmon-100 text-error"
              style={{ width: 950 }}
            >
              <WarningIcon className="w-4 h-4 mr-3" />
              <LicenseTypeError errorMessage={errorMessage} />
            </FadeInComponent>
          )}
        </FormControl>
      </div>
    </Tooltip>
  );
}

const StyledMenuItem = styled(MenuItem)`
  border: 0 !important;
  background-color: white;
  &.Mui-selected {
    background-color: white;
  }
  &.Mui-focusVisible {
    ${tw`bg-blue-100`}
  }
`;

function LicenseTypeError ({ errorMessage }: Pick<Props, 'errorMessage'>) {
  let message = 'pending invitations';
  if (errorMessage === UserEditError.NO_LICENSES_ADMIN) {
    message = 'pending admin invitations';
  } else if (errorMessage === UserEditError.NO_LICENSES_CONTRIBUTOR) {
    message = 'pending contributor invitations';
  } else if (errorMessage === UserEditError.NO_LICENSES_SUPPORT) {
    message = 'pending support invitations';
  }
  const pendingInvitations =
    <a target="_blank" className="underline" href="/admin/v3/users/search/permission_state=invited">
      {message}
    </a>;
  return (
    <div className="text-sm">
      {errorMessage}. Please check for any {pendingInvitations} that can be removed, or contact support to add additional licenses.
    </div>
  );
}

const HIDE_LICENSES_REMAINING_THRESHOLD = 100;

type ListItemProps = {
  children: ReactNode;
  description: string;
  licensesRemaining?: number;
}
function LicenseTypeListItem ({ children, description, licensesRemaining }: ListItemProps) {
  return (
    <div className="text-blue-900 break-words whitespace-normal grid gap-2.5 w-60">
      <div className="font-medium">
        { children }
      </div>
      {(licensesRemaining !== undefined && licensesRemaining < HIDE_LICENSES_REMAINING_THRESHOLD) && (
        <div className={`text-sm font-light ${licensesRemaining <= 0 ? 'text-error' : ''}`}>
          {Math.max(licensesRemaining, 0)} licenses remaining
        </div>
      )}
      <div className="text-sm font-light">{description}</div>
    </div>
  );
}
